import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { cn } from '@/utils/cn'
import { renderSimpleMarkdown } from '@/utils/simpleMarkdown'

import { dividerStyle, headingOptions, helpText, pageBreakTitle, staticTextOptions } from '../utils/layoutField'

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' } as const

/** The small grey hint under an input; renders nothing when the designer left it empty. */
export function FieldHelpText({ field, className }: { field: Pick<FormField, 'options'>; className?: string }) {
  const text = helpText(field)
  if (!text) return null
  return <div className={cn('text-xs text-neutral-500 space-y-1', className)}>{renderSimpleMarkdown(text)}</div>
}

/**
 * A layout element as the person filling in the form sees it. LINE_BREAK shows nothing: it only
 * pushes the next field onto a new row, which the caller's grid does with `col-span-full`.
 */
export function LayoutFieldView({ field }: { field: Pick<FormField, 'label' | 'options'> & { type?: string } }) {
  switch (field.type) {
    case FormFieldType.SECTION_HEADING: {
      const { text, subtitle, size } = headingOptions(field)
      const Heading = size
      return (
        <div className="pt-2">
          <Heading className={cn('font-semibold text-neutral-900', size === 'h2' ? 'text-lg' : 'text-base')}>{text || field.label}</Heading>
          {subtitle && <div className="mt-0.5 text-sm text-neutral-500 space-y-1">{renderSimpleMarkdown(subtitle)}</div>}
        </div>
      )
    }
    case FormFieldType.STATIC_TEXT: {
      const { text, align } = staticTextOptions(field)
      return <div className={cn('text-sm text-neutral-700 space-y-2', ALIGN[align])}>{renderSimpleMarkdown(text)}</div>
    }
    case FormFieldType.DIVIDER:
      return <hr className={cn('border-neutral-200', dividerStyle(field) === 'dashed' && 'border-dashed')} />
    case FormFieldType.PAGE_BREAK: {
      const title = pageBreakTitle(field)
      return title
        ? <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-widest text-neutral-400"><hr className="flex-1 border-neutral-200" />{title}<hr className="flex-1 border-neutral-200" /></div>
        : <hr className="border-neutral-200" />
    }
    default:
      return null
  }
}
